'use client'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import {
  CodeAuthRefreshTokensDocument,
  CodeAuthRefreshTokensMutationVariables,
  EducationLevelType,
  MeDocument,
  MeQuery,
  MeQueryVariables,
  Team,
  TeamIdNamesActiveFieldsFragment,
  useMeLazyQuery,
  UserFieldsFragment,
} from '@/graphql/generated/graphql'
import { WritableDraft } from 'immer/dist/internal'
import { immer } from 'zustand/middleware/immer'
import { configs } from '@/configs'
import { useCallback, useEffect } from 'react'
import {
  accessToken as accessTokenCookie,
  refreshToken as refreshTokenCookie,
} from './cookies'
import { client } from '@/app/Providers/ApolloContext'
import { PermissionBaseName, userHasPermissionFn } from '@/utils/permissions'
import {
  educationLevelTypeStore,
  selectedEducationLevelTypeFn,
} from '@/app/Providers/EducationLevelsContext/educationLevelState'
import { clearAllStorage } from '@/services/storageCleaner'
import { getLoginOrRegisterUrl } from '@/services/routes'

/**
 * O QUE LEVAR EM CONSIDERAÇÃO
 *
 * Acessando o site pelo URL
 * withREQUEST -> ✅ Access -> ✅
 * withREQUEST -> ❌ Access + ✅ Refresh -> ✅
 * withREQUEST -> ❌ Access + ❌ Refresh -> ❌
 * w/utREQUEST -> ❌ Access + ❌ Refresh -> ❌ (useAuthListener)
 */

// Z is Zustand, to make easy to find
type State = {
  zAccessToken: string | undefined // when stack layers, temp open is turned off and on again
  zRefreshToken: string | undefined // when stack layers, temp open is turned off and on again
  user: UserFieldsFragment | undefined
  /**
   * In case of maintainer admin
   */
  maintainerAdminSelectedTeamId: number | undefined
}

interface Actions {
  setState: (updater: (state: WritableDraft<State>) => void) => void
}

const initialState: State = {
  zAccessToken: undefined,
  zRefreshToken: undefined,
  user: undefined,
  maintainerAdminSelectedTeamId: undefined,
}

// Does not persist isRefreshing to avoid keep refreshing after reload page during refresh
let isRefreshing = false

const authState = create<State & Actions>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...initialState,
        setState: (e) => set(e, undefined, 'setState'),
      })),
      {
        name: 'authPersist',
        // getStorage: () => storage, // canot perform long here
        version: 2,
      },
    ),
    {
      name: 'zAuth',
      serialize: true,
      enabled: configs.IS_DEVELOPMENT,
      anonymousActionType: 'zAuthAnon',
    },
  ),
)

let autoLogoutTimeout: NodeJS.Timeout

export const logout = ({
  redirectPath = getLoginOrRegisterUrl({ isLogout: true, type: 'login' }),
}: { redirectPath?: string } = {}) => {
  authState.setState(initialState, true)
  clearAllStorage()
  client.clearStore()
  if (redirectPath) window.location.href = redirectPath
}

export const useLogout = () => {
  return {
    logout,
  }
}

// const isStaffSelector = (user: UserDataInToken | null | undefined) => {
//   const currentUserRole = user?.role
//   return currentUserRole === Role.Admin || currentUserRole === Role.Support
// }

const parseJwt = (token: string): { exp: number } | null => {
  const universalAtob = (b64Encoded: string) => {
    try {
      return atob(b64Encoded)
    } catch (err) {
      return Buffer.from(b64Encoded, 'base64').toString()
    }
  }

  if (!token) return null

  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      universalAtob(base64)
        .split('')
        .map(function (c) {
          return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
        })
        .join(''),
    )

    return JSON.parse(jsonPayload)
  } catch (err) {
    return null
  }
}

const handleAuthState = (refreshToken?: string) => {
  clearTimeout(autoLogoutTimeout)

  const refresh = refreshToken || authState.getState().zRefreshToken
  if (refresh) {
    const exp = parseJwt(refresh)?.exp
    if (!exp) return
    const remaining = exp * 1000 - Date.now()

    const TIMEOUT_MAX = 2147483647 // 24 days max https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values
    autoLogoutTimeout = setTimeout(() => {
      logout({})
    }, Math.min(remaining, TIMEOUT_MAX))
  }
}
authState.subscribe((newState, oldState) => {
  handleAuthState(newState.zRefreshToken)

  if (newState.zAccessToken && newState.zRefreshToken) {
    accessTokenCookie.write(undefined, newState.zAccessToken)
    refreshTokenCookie.write(undefined, newState.zRefreshToken)
  } else {
    // me() returns empty access/refresh
    // accessTokenCookie.delete()
    // refreshTokenCookie.delete()
  }
})

export const addSubscriber = (
  listener: (state: State, prevState: State) => void,
) => {
  if (typeof window === 'undefined') return
  return authState.subscribe((state, prevState) => {
    listener(state, prevState)
  })
}

export const getUserEmail = (): string | undefined => {
  const email = authState.getState().user?.email
  if (!email) return undefined

  return email
}

export const getAuthStateUser = () => {
  return authState.getState().user
}

// export const getIsStaff = () => {
//   const user = getAuthUser()
//   return isStaffSelector(user)
// }

export type AuthenticatedUser = UserFieldsFragment & {
  isAdmin: boolean
  permissions: PermissionBaseName[]
  selectedLevel: EducationLevelType
  hasPermission: (neededPermission: PermissionBaseName) => boolean
}
export type AuthUser = AuthenticatedUser | undefined
export const useAuthUser = (): AuthUser => {
  const selectedLevelStore = educationLevelTypeStore((s) => s.selectedLevel)

  const userFields = authState((s) => s.user)

  if (!userFields) return undefined

  const isAdmin = userFields?.email?.includes('@ledgertec.com.br') ?? false

  const permissions = (userFields?.permissions ?? []) as PermissionBaseName[]

  const Team = userFields?.Team as Team

  const selectedLevel = selectedEducationLevelTypeFn({
    user: userFields,
    selectedLevel: selectedLevelStore,
  })

  const user = {
    ...userFields,
    permissions,
    isAdmin,
    Team,
    selectedLevel,
    hasPermission: (neededPermission: PermissionBaseName, pure?: boolean) =>
      userHasPermissionFn({
        userPermissions: permissions,
        neededPermission,
        selectedLevel,
        pure,
      }),
  }

  return user
}

export const setUser = ({ user }: { user: UserFieldsFragment }) => {
  console.log('setUser', { user })
  authState.setState((old) => {
    old.user = user
  })
}

export const updateUserAuthMeNotObservable = async () => {
  const res = await client.query<MeQuery, MeQueryVariables>({
    query: MeDocument,
    fetchPolicy: 'network-only',
  })

  if (res.data?.me) {
    setUser({ user: res.data.me })
  }
}

export const useAuthMe = () => {
  const [run, data] = useMeLazyQuery()

  const update = useCallback(async () => {
    const res = await run()

    if (res.data?.me) {
      setUser({ user: res.data.me })
    }

    return res
  }, [run])

  return [update, data] as const
}

// export const useAuthChangePassword = () => {
//   const [recover, { loading }] = usePasswordRecoverMutation()

//   const changePassword = async (
//     variables: PasswordRecoverMutationVariables,
//     token: string,
//   ) => {
//     const logged = await recover({
//       variables,
//       context: {
//         headers: {
//           Authorization: `bearer ${token}`,
//         },
//       },
//     })
//     if (logged.data?.passwordRecover) {
//       toast('Senha alterada com sucesso', 'success')
//       Router.push('/minha-conta')
//     }
//   }

//   return { changePassword, loading }
// }

export const setTokens = ({
  access,
  refresh,
}: {
  access: string
  refresh: string
}) => {
  authState.setState((old) => {
    old.zAccessToken = access
    old.zRefreshToken = refresh
  })
}

const isTokenExpired = (token: string | undefined): boolean => {
  if (!token) return true
  const exp = parseJwt(token)?.exp

  if (!exp) return true

  if (Date.now() >= exp * 1000) {
    return true
  }
  return false
}

const setRefreshing = (newIsRefreshing: boolean) => {
  isRefreshing = newIsRefreshing
}

export const getAccessTokenRefreshedIfPossible = async (
  operationName?: string,
): Promise<string | undefined> => {
  const accessToken = accessTokenCookie.read()
  const refreshToken = refreshTokenCookie.read()

  const isAuthOperation = operationName?.startsWith('codeAuth')

  // Auth operations dont need token or refresh wait
  if (isAuthOperation) return undefined

  if (isRefreshing) {
    await new Promise((resolve) => setTimeout(resolve, 10))
    return getAccessTokenRefreshedIfPossible(operationName)
  }

  if (!accessToken || !refreshToken) return undefined

  const isAccessTokenExpired = isTokenExpired(accessToken)
  const isRefreshTokenExpired = isTokenExpired(refreshToken)
  // ok access
  if (accessToken && !isAccessTokenExpired) return accessToken
  // both expired
  if (isAccessTokenExpired && isRefreshTokenExpired) {
    logout({})
    throw new Error('Sessão inválida')
    return undefined
  }
  // access expired, refresh
  if (isAccessTokenExpired && refreshToken && !isRefreshTokenExpired) {
    const variables = {
      token: refreshToken,
    } satisfies CodeAuthRefreshTokensMutationVariables
    setRefreshing(true)
    // HERE RETURNS THE ACCESS TOKEN IN HEADER AND REPLACE IN MIDDLEWARE
    await client.mutate({
      mutation: CodeAuthRefreshTokensDocument,
      variables,
    })
    setRefreshing(false)
    return authState.getState().zAccessToken
  }
  // Access valid without refresh?

  return undefined // TODO: redirect to login again (backend requests using this breaks get static props of page)
}

/**
 * Keep user updated at every refresh
 */
export const useAuthAutoUpdater = () => {
  const updater = updateUserAuthMeNotObservable

  async function getMe(): Promise<void> {
    await updater()
  }

  useEffect(() => {
    getMe() // get latest token and update user on every f5
  }, [])

  return () => getMe()
}

export const setMaintainerAdminSelectedTeam = (id: number) => {
  authState.setState((old) => {
    old.maintainerAdminSelectedTeamId = id
  })
}

export const getUserTeams = (
  user?: UserFieldsFragment,
): TeamIdNamesActiveFieldsFragment[] => {
  const maintainerImAdminOrUndefined = user?.MaintainerAdmin?.Maintainer
  const maintainerImAdminTeams = maintainerImAdminOrUndefined?.Teams || []
  // user?.TeamMember(?) < for keeping compatibility with old version (as its a new property)
  const teamMemberTeams = user?.TeamMember?.map((el) => el.Team) || []

  const teamsWithDuplicates = [...maintainerImAdminTeams, ...teamMemberTeams]
  const teamsUnsorted = teamsWithDuplicates.filter(
    (team, index, self) =>
      team && self.findIndex((t) => t.id === team.id) === index,
  )
  const allTeams = teamsUnsorted.sort((a, b) => a.id - b.id)

  return allTeams
}

export const getUserMaintainerAdminSelectedTeam = ({
  user,
  maintainerAdminSelectedTeamId,
}: {
  user?: UserFieldsFragment
  maintainerAdminSelectedTeamId?: number
}): number | undefined => {
  const allTeams = getUserTeams(user)
  if (allTeams?.length === 0) return undefined
  const firstActiveTeam = allTeams.find((t) => t.isActive)?.id
  if (!maintainerAdminSelectedTeamId) return firstActiveTeam

  const allTeamIds = allTeams.map((t) => t.id)
  const has = allTeamIds.includes(maintainerAdminSelectedTeamId)
  console.log({ has, maintainerAdminSelectedTeamId })
  return has ? maintainerAdminSelectedTeamId : firstActiveTeam
}

export const useMaintainerAdminSelectedTeam = () => {
  const user = authState((s) => s.user)
  const maintainerAdminSelectedTeamId = authState(
    (s) => s.maintainerAdminSelectedTeamId,
  )

  return getUserMaintainerAdminSelectedTeam({
    user,
    maintainerAdminSelectedTeamId,
  })
}

export const getMaintainerAdminSelectedTeam = (): number | undefined => {
  return getUserMaintainerAdminSelectedTeam({
    user: authState.getState().user,
    maintainerAdminSelectedTeamId:
      authState.getState().maintainerAdminSelectedTeamId,
  })
}
